import React from 'react';

import { Text } from '@reckon-web/text';
import { Radio } from '@reckon-web/radio';
import { Stack } from '@reckon-web/stack';

import { Dialog } from '../components/common/dialog';
import { useDeleteJob as _useDeleteJob } from '../data/useDeleteJob';
import {
  processData,
  useJobRecurrenceHistoryList,
} from '../data/useJobRecurrenceHistoryList';
import { useUpdateJobRecurrance } from '../data/useUpdateJobRecurrance';

// all these little hooks don't need this BIG job
// They just need a specific subset of properties.
// but, the only way we can currently get to deleting a job is via the job form, which has done the getJob
// so, it'll be cached anyway
import { Job } from './use-query-job';
import { useDialog } from './use-dialog';
import { JobSignOffStatus } from './use-query-signoffs';

const JobSignOffRequestedDialog: React.FC<{
  onClose: (ok?: boolean) => void;
}> = ({ onClose }) => {
  return (
    <Dialog
      title="Delete job awaiting signoff?"
      cancelLabel="Cancel"
      onCancel={onClose}
      submitLabel="Yes, delete job"
      submitTone="critical"
      onSubmit={() => onClose(true)}
    >
      <Text>
        This job has been sent for sign off. Are you sure you want to delete
        this job?
      </Text>
    </Dialog>
  );
};

// I really want an enum here, because it's going to be used in a radio button
// eslint-disable-next-line no-restricted-syntax
enum DeleteRecurringJobResult {
  Cancel = 'cancel',
  DisableAndDelete = 'disable-and-delete',
  DeleteOnly = 'delete-only',
}

const JobRecursDialog: React.FC<{
  onClose: (result?: DeleteRecurringJobResult) => void;
}> = ({ onClose }) => {
  const [selection, setSelection] = React.useState<DeleteRecurringJobResult>(
    DeleteRecurringJobResult.DeleteOnly
  );

  return (
    <Dialog
      title={'Delete job and action recurrence?'}
      cancelLabel="Cancel"
      onCancel={onClose}
      submitLabel="Yes, delete and action"
      submitTone="critical"
      onSubmit={() => onClose(selection)}
    >
      <Text>
        The job you are deleting forms part of a recurrence sequence and you are
        deleting the last active recurrence of the job. What would you like to
        do with the recurrence?
      </Text>
      <Stack
        background="muted"
        borderRadius="small"
        gap="small"
        padding="medium"
        role="radiogroup"
      >
        <Radio
          value={DeleteRecurringJobResult.DeleteOnly}
          checked={selection === DeleteRecurringJobResult.DeleteOnly}
          onChange={() => setSelection(DeleteRecurringJobResult.DeleteOnly)}
        >
          <Text inline leading="tighter" weight="semibold">
            Leave recurrence
          </Text>{' '}
          and delete job only. The previous job in this recurrence sequence will
          be able to be recurred.
        </Radio>
        <Radio
          value={DeleteRecurringJobResult.DisableAndDelete}
          checked={selection === DeleteRecurringJobResult.DisableAndDelete}
          onChange={() =>
            setSelection(DeleteRecurringJobResult.DisableAndDelete)
          }
        >
          <Text inline leading="tighter" weight="semibold">
            Disable recurrence
          </Text>{' '}
          and delete job. This job will no longer be able to be recurred.
        </Radio>
      </Stack>
    </Dialog>
  );
};

const ConfirmationDialog: React.FC<{
  onClose: (ok?: boolean) => void;
}> = ({ onClose }) => {
  return (
    <Dialog
      title="Delete this job?"
      cancelLabel="Cancel"
      onCancel={onClose}
      submitLabel="Yes, delete job"
      submitTone="critical"
      onSubmit={() => onClose(true)}
    >
      <Text>This is a permanent action that cannot be undone.</Text>
    </Dialog>
  );
};

export const useDeleteJob = () => {
  const [_deleteJob] = _useDeleteJob(); // TODO: handle loading and error states
  const [updateJobRecurrance] = useUpdateJobRecurrance();
  const { refetch: getRecurrenceHistory } = useJobRecurrenceHistoryList({
    skip: true,
  });

  const [Dialog, openDialog] = useDialog();

  const deleteJob = React.useCallback(
    async (job: Job) => {
      // "job has been sent for review" prompt
      if (
        job.signOffStatus === JobSignOffStatus.Requested &&
        !(await openDialog<boolean>(JobSignOffRequestedDialog, {
          size: 'small',
        }))
      ) {
        return;
      }

      const jobId = job.cloudId || '';

      // "job recurs" prompt
      if (job.recurrence?.enabled) {
        const recurrenceId = job.recurrence.cloudId || '';

        const historyResult = await getRecurrenceHistory({
          recurrenceId,
        });
        const history = processData(historyResult.data) || [];
        const nonDeletedHistory = history.filter((x) => !x.WfDeletedOnUTC);

        // only want to prompt if the job has been recurred
        const hasMoreThanOneRecurrence = nonDeletedHistory.length > 1;

        // only want to prompt if this is the most recently created
        const isMostRecentRecurrence = !nonDeletedHistory.some(
          (x) =>
            x.cloudId !== job.cloudId && x.WfCreatedOnUTC! > job.WfCreatedOnUTC!
        );
        if (hasMoreThanOneRecurrence && isMostRecentRecurrence) {
          const confirmRecurDeleteResult =
            await openDialog<DeleteRecurringJobResult>(JobRecursDialog, {
              size: 'small',
            });
          if (
            !confirmRecurDeleteResult ||
            confirmRecurDeleteResult === DeleteRecurringJobResult.Cancel
          ) {
            // they changed their mind. Bail
            return;
          }
          if (
            confirmRecurDeleteResult ===
            DeleteRecurringJobResult.DisableAndDelete
          ) {
            // disable the recurrence before deleting
            const recurranceResult = await updateJobRecurrance({
              variables: {
                input: {
                  cloudId: job.cloudId || '', // yes, this is the job id. For... reasons
                  enabled: false,
                  nextDateUtc: null,
                  recurrencefrequency: null,
                },
              },
            });
            if (recurranceResult.errors) {
              // uh oh. Pass the errors back out to the caller and bail
              return recurranceResult;
            }
          }
          // finally, delete the job and bail. Bypass the normal delete prompt
          return _deleteJob({ variables: { cloudId: jobId } });
        }
      }

      // "standard" delete prompt
      if (!(await openDialog<boolean>(ConfirmationDialog, { size: 'small' }))) {
        return;
      }

      // got this far, time to delete the job
      return _deleteJob({ variables: { cloudId: jobId } });
    },
    [_deleteJob, openDialog, getRecurrenceHistory, updateJobRecurrance]
  );

  return React.useMemo(
    () => [Dialog, deleteJob] as [typeof Dialog, typeof deleteJob],
    [Dialog, deleteJob]
  );
};
